<template>
    <SelectRoot>
        <SelectTrigger v-bind="field?.controlAttrs.value" :id="id ?? field?.controlId" class="select-trigger" :class="triggerClass" :aria-label="ariaLabel">
            <i v-if="icon" :class="icon" class="bi text-base/none" aria-hidden="true"></i>
            <span v-if="prefix" class="body-small shrink-0 text-ink-muted" aria-hidden="true">{{ prefix }}</span>
            <SelectValue v-slot="{ modelValue }" class="body-small min-w-0 grow truncate text-start" :placeholder="placeholder">
                {{ selectedLabel(modelValue) || placeholder }}
            </SelectValue>
            <SelectIcon class="flex text-base/none">
                <i class="bi bi-chevron-down" aria-hidden="true"></i>
            </SelectIcon>
        </SelectTrigger>
        <SelectPortal :disabled="disablePortal">
            <SelectContent
                class="rounded-xl border border-control-border text-control-text bg-control-bg w-(--reka-select-trigger-width) min-w-64 overflow-hidden p-2.5 shadow-elevated z-40"
                :class="[dropdownClasses.content, contentClass]"
                position="popper"
                side="bottom"
                :side-offset="9"
                :force-mount="disablePortal">
                <ScrollAreaRoot type="auto" class="w-full h-full" :class="dropdownClasses.scrollArea">
                    <SelectViewport as-child>
                        <ScrollAreaViewport class="w-full h-full" :class="dropdownClasses.viewport">
                            <SelectItem
                                v-for="option in options"
                                :key="String(option.key)"
                                class="px-4 py-2 mb-1 last:mb-0 rounded-lgr cursor-pointer hover:bg-select-option-hover data-highlighted:bg-select-option-hover data-[state=checked]:bg-select-option-selected flex justify-between items-center -outline-offset-1"
                                :value="option.key">
                                <SelectItemText class="body-small text-ink">
                                    {{ option.label }}
                                </SelectItemText>
                                <SelectItemIndicator class="h-5">
                                    <i class="bi bi-check text-control-accent text-lg/none" aria-hidden="true"></i>
                                </SelectItemIndicator>
                            </SelectItem>
                        </ScrollAreaViewport>
                        <ScrollAreaScrollbar orientation="vertical" class="w-3.5 pr-2 mt-2">
                            <ScrollAreaThumb class="bg-control-border w-1.25 rounded-full"></ScrollAreaThumb>
                        </ScrollAreaScrollbar>
                    </SelectViewport>
                </ScrollAreaRoot>
            </SelectContent>
        </SelectPortal>
    </SelectRoot>
</template>

<script setup lang="ts">
import { useFormField } from "~vue/components/form/formFieldContext";
import type { AcceptableValue } from "reka-ui";
import {
    ScrollAreaRoot,
    ScrollAreaScrollbar,
    ScrollAreaThumb,
    ScrollAreaViewport,
    SelectContent,
    SelectIcon,
    SelectItem,
    SelectItemIndicator,
    SelectItemText,
    SelectPortal,
    SelectRoot,
    SelectTrigger,
    SelectValue,
    SelectViewport,
} from "reka-ui";
import { computed, useCssModule } from "vue";

// Field wiring from a surrounding FormField, when present: the trigger takes the
// context id so the FormField label's `for` reaches it, plus describedby/invalid.
const field = useFormField();

const {
    options = [],
    disablePortal = false,
    dropdownSize = "default",
} = defineProps<{
    /** Array of options to display in the dropdown. The 'key' property is used as the model value. */
    options?: Array<{ key: AcceptableValue; label: string }>;

    /** Id for the trigger, so a <label for> can name it. SelectRoot renders no element of its
     * own, so an id passed as a plain attribute would be dropped rather than land on the control. */
    id?: string;

    /** Optional icon class to display on the trigger box.  Ex: `bi-person-fill`. */
    icon?: string;

    /** Can be used to add additional classes to the trigger component. */
    triggerClass?: string;

    /** Can be used to add additional classes to the dropdown panel. */
    contentClass?: string;

    /** Short inline caption shown before the value, e.g. "Sort:" reads as "Sort: Name A–Z".
     * Visual only; the trigger's accessible name still comes from `ariaLabel` or the FormField label. */
    prefix?: string;

    /** Optional placeholder to be displayed when no value is selected. */
    placeholder?: string;

    /** Accessible name for the trigger. Required whenever the visible caption is a
     * sibling element rather than a `<label for>`, since the trigger is a button and
     * picks up no name from adjacent text. */
    ariaLabel?: string;

    /** Maximum dropdown height; tall displays more options before scrolling. */
    dropdownSize?: "default" | "tall";

    /** Renders the dropdown options inline; mostly useful for testing. */
    disablePortal?: boolean;
}>();

const styles = useCssModule();
const dropdownSizeClasses: Record<"default" | "tall", { content: string; scrollArea: string; viewport: string }> = {
    default: { content: "max-h-60", scrollArea: "", viewport: "max-h-60" },
    tall: { content: styles.tallContent, scrollArea: "min-h-0 flex flex-1 flex-col", viewport: "min-h-0 max-h-full" },
};
const dropdownClasses = computed(() => dropdownSizeClasses[dropdownSize]);

// reka-ui's SelectValue shows the text each option had when it first rendered, so a label that
// changes later (a locale catalogue loading after mount) would leave the trigger stale.
function selectedLabel(value: AcceptableValue | AcceptableValue[] | undefined) {
    return (Array.isArray(value) ? value : [value])
        .map((selected) => options.find((option) => option.key === selected)?.label)
        .filter(Boolean)
        .join(", ");
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .select-trigger {
        @apply w-full rounded-lg p-2.5 flex items-center border border-control-border text-control-text disabled:text-control-disabled-text gap-1 bg-control-bg transition-colors enabled:hover:border-control-border-strong;
    }
}
</style>

<style module>
@reference "../../../css/tailwind.css";

@layer components {
    .tallContent {
        max-height: min(20rem, var(--reka-select-content-available-height));
    }
}
</style>
